import React, { useState, useEffect } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import PropTypes from 'prop-types';
import MenuItem from '@material-ui/core/MenuItem';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Card,
  CardTitle,
  CardDescription,
  PrimaryButton,
  LinkButton,
  InfoBox,
  CardBodyLeft,
  AlertRow,
  SectionLabel,
  ButtonRow,
  OutlineButton,
  StyledTextField,
} from '../styledComponents';
import { getCities, getNearestCity, createCity } from '../api';
import { TYPES, useGlobalActions } from '../../../context/globalActions';

export default function StepUbicacion({ onNext, onSubLabel }) {
  const history = useHistory();
  const location = useLocation();
  const [view, setView] = useState('permiso');

  const [cities, setCities] = useState([]);
  const [cityId, setCityId] = useState('');
  const [loadingCities, setLoadingCities] = useState(true);
  const [citiesError, setCitiesError] = useState(null);
  const [locating, setLocating] = useState(false);
  const [newCityName, setNewCityName] = useState('');
  const [creatingCity, setCreatingCity] = useState(false);
  const { handleOpenSnackbar } = useGlobalActions();

  useEffect(() => {
    getCities()
      .then(data => setCities(data))
      .catch(() =>
        setCitiesError('No pudimos cargar las ciudades. Intenta de nuevo.'),
      )
      .finally(() => setLoadingCities(false));
  }, []);

  const goToEstado = () => {
    setView('estado');
    onSubLabel('ESTADO');
  };

  const goBack = () => {
    setView('permiso');
    onSubLabel('UBICACIÓN');
  };

  const handleRequestLocation = () => {
    setLocating(true);

    if (!navigator.geolocation) {
      goToEstado();
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async position => {
        const { latitude, longitude } = position.coords;
        setLocating(true);
        try {
          const response = await getNearestCity({ latitude, longitude });
          if (response?.data) {
            const params = new URLSearchParams(location.search);
            params.set('cityCodeName', response.data);
            history.replace({ search: params.toString() });
            onNext({
              locationMethod: 'geo',
              cityCodeName: response.data,
            });
          } else {
            goToEstado();
          }
        } catch {
          goToEstado();
        } finally {
          setLocating(false);
        }
      },
      () => {
        goToEstado();
        setLocating(false);
      },
      { timeout: 7000, maximumAge: 0, enableHighAccuracy: true },
    );
  };

  const handleCreateCity = async () => {
    let codeName = cityId;
    if (cityId === '__otro__') {
      setCreatingCity(true);
      try {
        const response = await createCity({
          name: newCityName.trim(),
        });
        codeName = response;
      } catch (error) {
        handleOpenSnackbar(
          error?.response?.data?.message ?? 'Algo salió mal, intenta de nuevo.',
          TYPES.ERROR,
        );
        setCreatingCity(false);
        return;
      }
      setCreatingCity(false);
    }
    const params = new URLSearchParams(location.search);
    params.set('cityCodeName', codeName);
    history.replace({ search: params.toString() });
    onNext({ locationMethod: 'manual', cityCodeName: codeName });
  };

  if (view === 'estado') {
    return (
      <Card>
        <CardBodyLeft>
          <AlertRow>
            <InfoOutlinedIcon style={{ fontSize: 28, color: '#D97706' }} />
            <CardTitle>No pudimos obtener tu ubicación</CardTitle>
          </AlertRow>
          <CardDescription>
            No hay problema — solo cuéntanos dónde estás para continuar.
          </CardDescription>

          <div>
            <SectionLabel>¿En qué ciudad resides?</SectionLabel>
            <StyledTextField
              select
              name="cityId"
              label={loadingCities ? 'Cargando...' : 'Ciudad'}
              value={cityId}
              onChange={e => setCityId(e.target.value)}
              variant="filled"
              disabled={loadingCities || !!citiesError}
              style={{ marginTop: 8 }}
            >
              {cities.map(city => (
                <MenuItem key={city.id} value={city.codeName}>
                  {city.name}
                </MenuItem>
              ))}
              <MenuItem value="__otro__">Otro</MenuItem>
            </StyledTextField>
            {cityId === '__otro__' && (
              <StyledTextField
                name="newCityName"
                label="Nombre de tu ciudad"
                value={newCityName}
                onChange={e => setNewCityName(e.target.value)}
                variant="filled"
                style={{ marginTop: 8 }}
              />
            )}
            {citiesError && (
              <p style={{ color: '#d32f2f', fontSize: 13, margin: '6px 0 0' }}>
                {citiesError}
              </p>
            )}
          </div>

          <ButtonRow>
            <OutlineButton onClick={goBack}>
              <ArrowBackIcon style={{ fontSize: 16 }} />
              Atrás
            </OutlineButton>
            <PrimaryButton
              onClick={handleCreateCity}
              disabled={
                !cityId ||
                (cityId === '__otro__' && !newCityName.trim()) ||
                creatingCity
              }
            >
              {creatingCity ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <>
                  Continuar
                  <ArrowForwardIcon style={{ fontSize: 16 }} />
                </>
              )}
            </PrimaryButton>
          </ButtonRow>
        </CardBodyLeft>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <LocationOnIcon style={{ fontSize: 36, color: '#00843d' }} />
        <CardTitle>Antes de continuar...</CardTitle>
        <CardDescription>
          Con el fin de mostrarte la sucursal más cercana, por favor permite el
          acceso a tu ubicación.
        </CardDescription>
        <PrimaryButton onClick={handleRequestLocation} disabled={locating}>
          {locating ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            'Pedir permiso de ubicación'
          )}
        </PrimaryButton>
        <LinkButton onClick={goToEstado}>
          Prefiero elegir mi ciudad manualmente
        </LinkButton>
      </Card>
      <InfoBox>
        <strong>¿Por qué pedimos ubicación?</strong> La usamos solo para
        encontrar la sucursal PrestaVale más cercana. Nunca la guardamos ni la
        compartimos.
      </InfoBox>
    </>
  );
}

StepUbicacion.propTypes = {
  onNext: PropTypes.func.isRequired,
  onSubLabel: PropTypes.func.isRequired,
};
